// TODO: remove with https://github.com/combinaut/sparkle/issues/32203 once page-builder controls are sp apps.
import { useEventListener } from "@vueuse/core";
import { createApiClient } from "#/shared/api/client.js";
import { settleMount } from "#ce/framework/mount-ready";
import { isPresent, toBoolean } from "#ce/utils/props";

// Requests still in flight, by URL, so pickers that load the same URL together share one request. An entry goes when
// its request settles, so a failed load is requested again.
const pendingRequests = new Map();

// The `select-items` hosts already given their items, so a repeated decoration leaves them as they are.
const decoratedSelects = new WeakSet();

function requestItems(url) {
  if (!pendingRequests.has(url)) {
    const client = createApiClient({
      parse: false,
      baseURL: location.origin,
    });
    const request = client.get(url);

    function forgetRequest() {
      pendingRequests.delete(url);
    }

    pendingRequests.set(url, request);
    request.then(forgetRequest, forgetRequest);
  }

  return pendingRequests.get(url);
}

// Returns the rows of `groups` in order: each group's header, if it has one, then its rows. A group without rows
// is left out.
function flattenGroups(groups) {
  return [...groups.values()]
    .filter((group) => group.items.length > 0)
    .flatMap((group) => (group.header ? [group.header, ...group.items] : group.items));
}

async function requestItemPages(requestUrl, allPages) {
  let first;
  let offset = Number(requestUrl.searchParams.get("offset") ?? 0);
  // Rows by the header they sit under, in the order the headers first appear; null holds the rows before any header.
  const groups = new Map();
  const values = new Set();
  const retained = new Map();

  function groupFor(header, headerItem = null) {
    if (!groups.has(header)) {
      groups.set(header, { header: headerItem, items: [] });
    }

    return groups.get(header);
  }

  while (true) {
    const page = await requestItems(requestUrl.toString());

    if (!Array.isArray(page?.items) || !Object.hasOwn(page, "value")) {
      return null;
    }

    if (!allPages) {
      return page;
    }

    first ??= page;

    const pageValues = Array.isArray(page.page_values) ? new Set(page.page_values.map(String)) : null;
    let group = groupFor(null);

    for (const item of page.items) {
      if (Object.hasOwn(item, "header")) {
        group = groupFor(item.header, item);
        continue;
      }

      const value = Object.hasOwn(item, "value") ? String(item.value) : undefined;

      if (pageValues && value && !pageValues.has(value)) {
        retained.set(value, { item, group });
        continue;
      }

      if (value === undefined || !values.has(value)) {
        group.items.push(item);
        values.add(value);
      }
    }

    if (!Object.hasOwn(page, "next_offset")) {
      return null;
    }

    const next = page.next_offset;

    if (next === null) {
      for (const [value, { item, group: retainedGroup }] of retained) {
        if (!values.has(value)) {
          retainedGroup.items.push(item);
        }
      }

      return { ...first, items: flattenGroups(groups), next_offset: null };
    }

    if (!Number.isSafeInteger(next) || next <= offset) {
      return null;
    }

    offset = next;
    requestUrl.searchParams.set("offset", String(next));
  }
}

function itemValues(items) {
  return items.flatMap((item) => [String(item.value), ...itemValues(item.children || [])]);
}

// The control's own value when it has one and the items list all of it, otherwise `fallback`.
function listedOwnValue(control, items, fallback) {
  const own = [].concat(control.value ?? []).map(String);
  const listed = new Set(itemValues(items));

  if (own.length === 0 || !own.every((entry) => listed.has(entry))) {
    return fallback;
  }

  return control.value;
}

/**
 * Installs the server's Quantum items and value; returns null if fetching, the payload, or mounting fails. Pickers
 * loading the same URL at the same time share one request. Without a `selected` value in the URL, a control keeps its
 * own value when the items list it. `allPages` follows `next_offset` batches and deduplicates rows with the same stringified value.
 * Edits made while loading take precedence over the response.
 * With `allPages`, each batch must provide an advancing safe integer `next_offset`, or `null` to end the list. Missing or invalid offsets
 * return null. The control is updated only after every batch succeeds.
 */
export async function loadSelectItems(
  control,
  url,
  { selected, includeBlank, section, defaultChoice, allPages = false } = {},
) {
  let stopListening;

  try {
    if (!(await settleMount(control))) {
      return null;
    }

    const initialValue = JSON.stringify(control.value);
    let userEdited = false;

    function rememberSelectionEdit() {
      userEdited = true;
    }

    stopListening = useEventListener(control, "change", rememberSelectionEdit);
    const requestUrl = new URL(url, location.origin);

    requestUrl.searchParams.set("api", "quantum");
    requestUrl.searchParams.set("multiple", String(isPresent(control.multiple ?? control.getAttribute("multiple"))));

    if (selected !== undefined) {
      requestUrl.searchParams.set("selected", JSON.stringify(selected));
    }

    if (includeBlank !== undefined) {
      requestUrl.searchParams.set("include_blank", String(includeBlank));
    }

    if (section) {
      requestUrl.searchParams.set("sections", section);
    }

    if (defaultChoice) {
      requestUrl.searchParams.set("default_choice", defaultChoice);
    }

    const payload = await requestItemPages(requestUrl, allPages);

    if (!Array.isArray(payload?.items) || !Object.hasOwn(payload, "value")) {
      return null;
    }

    if (!(await settleMount(control))) {
      return null;
    }

    const edited = userEdited || JSON.stringify(control.value) !== initialValue;
    let value = control.value;

    if (!edited) {
      value = requestUrl.searchParams.has("selected")
        ? payload.value
        : listedOwnValue(control, payload.items, payload.value);
    }

    control.items = structuredClone(payload.items);
    control.value = value;

    return { ...payload, value };
  } catch (error) {
    console.error("Admin select items failed to load", control, error);

    return null;
  } finally {
    stopListening?.();
  }
}

/** Loads each `select-items` sp-select under root from its `data-items-url`, once per control. */
export function initializeSelectItems(root) {
  for (const control of root?.querySelectorAll("sp-select[data-behaviour~=select-items]") || []) {
    if (decoratedSelects.has(control)) {
      continue;
    }

    decoratedSelects.add(control);
    loadSelectItems(control, control.dataset.itemsUrl, { allPages: toBoolean(control.dataset.itemsAllPages) }).then(
      function forgetFailedLoad(payload) {
        if (payload === null) {
          decoratedSelects.delete(control);
        }
      },
    );
  }
}
